import { DeleteOutlined } from '@ant-design/icons'
import { Popconfirm } from 'antd'
import { ColumnType } from 'antd/es/table'
import { Key, Suspense } from 'react'
import { z } from 'zod'
const NomenclatureEditModal = safeLazy(
  () =>
    import(
      '@/components/EntityEditModals/NomenclatureEditModal/NomenclatureEditModal'
    )
)

import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import AtomTreeSelect from '@/components/Atoms/AtomTreeSelect/AtomTreeSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import { barcodeSchema } from '@/schemas'
import {
  AntdTreeType,
  BrandInterface,
  CategoryInterface,
  CountryInterface,
  NomenclatureGroupInterface,
  NomenclatureType,
  OptionType,
  PluralNomenclatureImportResponseType,
  TableCellType,
  UnitOfMeasurementInterface,
} from '@/types'
import { safeLazy } from '@/utils/app/safeLazy'
import handleChangeNumberInput from '@/utils/forms/handleChangeNumberInput'
import { getBarcodeError, sanitizeBarcode } from '@/utils/nomenclature/barcode'
import handleSelectTreeNode from '@/utils/tree/selection/handleSelectTreeNode'

import cn from './PluralImport.module.scss'

export const parsedDataPluralModificationSchema = z.object({
  brand: z
    .object({
      value: z.string().trim(),
      label: z.string().trim(),
    })
    .optional(),
  category: z.string().trim().optional(),
  nomenclatureGroup: z.string().trim().optional(),
  barcode: barcodeSchema.optional(),
  countryOfOrigin: z
    .object({
      value: z.string().trim(),
      label: z.string().trim(),
    })
    .optional(),
  itemLength: z.string().trim().optional(),
  itemWidth: z.string().trim().optional(),
  itemHeight: z.string().trim().optional(),
  itemWeight: z.string().trim().optional(),
  warranty: z.string().trim().optional(),
  unitOfMeasurement: z
    .object({
      value: z.string().trim(),
      label: z.string().trim(),
    })
    .optional(),
  details: z.string().trim().optional(),
  comment: z.string().trim().optional(),
})

export type ParsedDataPluralModificationType = z.infer<
  typeof parsedDataPluralModificationSchema
>

const renderMatchedValue = (record: NomenclatureType, columnKey: string) => {
  const valueObj = record[columnKey] as OptionType | undefined
  const rawKey = `raw${columnKey.charAt(0).toUpperCase() + columnKey.slice(1)}`
  const rawValue = record[rawKey] as string | undefined

  if (!valueObj?.value && rawValue) {
    return (
      <div className={cn.notFoundValue}>
        <span className={cn.notFoundTitle}>Не найдено!</span>
        <div className={cn.notFoundSource}>
          Из Excel: <span className={cn.notFoundRawValue}>"{rawValue}"</span>
        </div>
        <div className={cn.notFoundHint}>
          Убедитесь, что на <b>Шаге 3</b> выбрана верная колонка.
        </div>
      </div>
    )
  }

  return <span className={cn.propLabel}>{valueObj?.label || ''}</span>
}

const handleOnCell = (handleOnCellParams: {
  record: NomenclatureType
  columnKey: string
  selectedCells: TableCellType[]
  handleClickCell: (
    rowKey: string,
    columnKey: string,
    e: React.MouseEvent
  ) => void
  setPropertyInOneCellSelected: (
    propertyInOneCellSelected: OptionType | null
  ) => void
}) => {
  const {
    record,
    columnKey,
    selectedCells,
    handleClickCell,
    setPropertyInOneCellSelected,
  } = handleOnCellParams

  const rawKey = `raw${columnKey.charAt(0).toUpperCase() + columnKey.slice(1)}`
  const isNotFound =
    !(record[columnKey] as OptionType | undefined)?.value && record[rawKey]

  return {
    onClick: (e: React.MouseEvent) => {
      handleClickCell(String(record.key), columnKey, e)
      setPropertyInOneCellSelected(record[columnKey] as OptionType)
    },
    style: isNotFound
      ? { backgroundColor: '#fff2f0', border: '1px solid #ffccc7' }
      : {},
    className: selectedCells.some(
      (cell) => cell.rowKey == record.key && cell.columnKey == columnKey
    )
      ? cn.highlighted
      : '',
  }
}

export const getColumns = (getColumnsParams?: {
  selectedCells: TableCellType[]
  brands: BrandInterface[]
  countries: CountryInterface[]
  unitsOfMeasurement: UnitOfMeasurementInterface[]
  categoriesTree: CategoryInterface[]
  antdCategoriesTree: AntdTreeType[]
  nomenclatureGroupsTree: NomenclatureGroupInterface[]
  antdNomenclatureGroupsTree: AntdTreeType[]
  parsedData: NomenclatureType[]
  oneCellSelected: TableCellType
  propertyInOneCellSelected: OptionType | string | null
  setPropertyInOneCellSelected: (
    propertyInOneCellSelected: OptionType | string | null
  ) => void
  handleClickCell: (
    rowKey: string,
    columnKey: string,
    e: React.MouseEvent
  ) => void
  setParsedData: (parsedData: NomenclatureType[]) => void
  removeTableRow: (rowKey: Key) => void
}) =>
  [
    {
      title: '№',
      key: 'index',
      fixed: 'left',
      render: (_: undefined, __: undefined, index: number) => {
        return <span>{index + 1}</span>
      },
    },
    {
      title: 'Артикул',
      key: 'article',
      dataIndex: 'article',
      fixed: 'left',
    },
    {
      title: 'Артикул (поставщика)',
      key: 'supplierArticle',
      dataIndex: 'supplierArticle',
    },
    {
      title: 'Наименование',
      key: 'description',
      fixed: 'left',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'description',
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            parsedData,
            setParsedData,
            setPropertyInOneCellSelected,
            propertyInOneCellSelected,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'description'
          ) {
            return (
              <textarea
                defaultValue={propertyInOneCellSelected as string}
                onBlur={(e) => {
                  const val = e.target.value
                  setPropertyInOneCellSelected(val)
                  const newData = [...parsedData]
                  newData.forEach((n) => {
                    if (n.key == record.key) n.description = val
                  })
                  setParsedData(newData)
                }}
                className={cn.editableCellTextarea}
              />
            )
          }
          return <span>{record.description}</span>
        }
      },
    },
    {
      title: 'Бренд *',
      key: 'brand',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'brand',
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            brands,
            parsedData,
            propertyInOneCellSelected,
            setPropertyInOneCellSelected,
            setParsedData,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'brand'
          ) {
            return (
              <HoverElement>
                <AtomSelect
                  options={brands.map((brand) => ({
                    value: brand.id,
                    label: brand.description,
                  }))}
                  openByHover={false}
                  value={
                    propertyInOneCellSelected &&
                    typeof propertyInOneCellSelected !== 'string'
                      ? propertyInOneCellSelected.value
                      : (propertyInOneCellSelected as string)
                  }
                  showSearch
                  handleChange={(_, option: OptionType | OptionType[]) => {
                    if (!Array.isArray(option)) {
                      const newParsedData = [...parsedData]
                      newParsedData.forEach((product) => {
                        if (product.key == record.key) {
                          product.brand = {
                            value: option.value,
                            label: option.label,
                          }
                          product.rawBrand = ''
                        }
                      })
                      setPropertyInOneCellSelected({
                        value: option.value,
                        label: option.label,
                      })
                      setParsedData(newParsedData)
                    }
                  }}
                  handleClear={() => {
                    const newParsedData = [...parsedData]
                    newParsedData.forEach((p) => {
                      if (p.key == record.key)
                        p.brand = { value: '', label: '' }
                    })
                    setPropertyInOneCellSelected(null)
                    setParsedData(newParsedData)
                  }}
                />
              </HoverElement>
            )
          }
          return renderMatchedValue(record, 'brand')
        }
      },
    },
    {
      title: 'Категория *',
      key: 'category',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'category',
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            categoriesTree,
            antdCategoriesTree,
            parsedData,
            oneCellSelected,
            propertyInOneCellSelected,
            setPropertyInOneCellSelected,
            setParsedData,
          } = getColumnsParams
          if (
            categoriesTree.length &&
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'category'
          ) {
            return (
              <HoverElement>
                <AtomTreeSelect
                  tree={categoriesTree}
                  antdTree={antdCategoriesTree}
                  value={
                    propertyInOneCellSelected &&
                    typeof propertyInOneCellSelected != 'string'
                      ? propertyInOneCellSelected.value
                      : ''
                  }
                  openByHover={false}
                  handleChange={(tree, node) => {
                    const path = handleSelectTreeNode(tree, node)
                    const names = path?.split(' > ')
                    const newParsedData = [...parsedData]
                    if (names) {
                      newParsedData.forEach((item) => {
                        if (item.key == record.key) {
                          const label = names[names.length - 1]
                          item.category = { value: node, label }
                          setPropertyInOneCellSelected({ value: node, label })
                        }
                      })
                    }
                    setParsedData(newParsedData)
                  }}
                  handleClear={() => {
                    const newParsedData = [...parsedData]
                    newParsedData.forEach((item) => {
                      if (item.key == record.key)
                        item.category = { value: '', label: '' }
                    })
                    setPropertyInOneCellSelected(null)
                    setParsedData(newParsedData)
                  }}
                />
              </HoverElement>
            )
          }
          const pathToCategory = handleSelectTreeNode(
            categoriesTree,
            record.category.value
          )
          return (
            <div className={cn.labelWithPath}>
              <span>{record.category.label}</span>
              <div className={cn.pathToTreeLeaf}>{pathToCategory}</div>
            </div>
          )
        }
      },
    },
    {
      title: 'Группа номенклатуры *',
      key: 'nomenclatureGroup',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'nomenclatureGroup',
            selectedCells,
            setPropertyInOneCellSelected,
            handleClickCell,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            nomenclatureGroupsTree,
            oneCellSelected,
            antdNomenclatureGroupsTree,
            parsedData,
            propertyInOneCellSelected,
            setPropertyInOneCellSelected,
            setParsedData,
          } = getColumnsParams
          if (
            nomenclatureGroupsTree.length &&
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'nomenclatureGroup'
          ) {
            return (
              <HoverElement>
                <AtomTreeSelect
                  tree={nomenclatureGroupsTree}
                  antdTree={antdNomenclatureGroupsTree}
                  value={
                    propertyInOneCellSelected &&
                    typeof propertyInOneCellSelected != 'string'
                      ? propertyInOneCellSelected.value
                      : ''
                  }
                  openByHover={false}
                  handleChange={(tree, node) => {
                    const path = handleSelectTreeNode(tree, node)
                    const names = path?.split(' > ')
                    const newParsedData = [...parsedData]
                    if (names) {
                      newParsedData.forEach((item) => {
                        if (item.key == record.key) {
                          const label = names[names.length - 1]
                          item.nomenclatureGroup = { value: node, label }
                          setPropertyInOneCellSelected({ value: node, label })
                        }
                      })
                    }
                    setParsedData(newParsedData)
                  }}
                  handleClear={() => {
                    const newParsedData = [...parsedData]
                    newParsedData.forEach((item) => {
                      if (item.key == record.key)
                        item.nomenclatureGroup = { value: '', label: '' }
                    })
                    setPropertyInOneCellSelected(null)
                    setParsedData(newParsedData)
                  }}
                />
              </HoverElement>
            )
          }
          const path = handleSelectTreeNode(
            nomenclatureGroupsTree,
            record.nomenclatureGroup.value
          )
          return (
            <div className={cn.labelWithPath}>
              <span>{record.nomenclatureGroup.label}</span>
              <div className={cn.pathToTreeLeaf}>{path}</div>
            </div>
          )
        }
      },
    },
    {
      title: 'Длина, см',
      key: 'itemLength',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'itemLength',
            selectedCells,
            setPropertyInOneCellSelected,
            handleClickCell,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            parsedData,
            setParsedData,
            setPropertyInOneCellSelected,
            propertyInOneCellSelected,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'itemLength'
          ) {
            return (
              <input
                defaultValue={propertyInOneCellSelected as string}
                onBlur={(e) => {
                  const val = e.target.value
                  setPropertyInOneCellSelected(val)
                  const newData = [...parsedData]
                  newData.forEach((n) => {
                    if (n.key == record.key) n.itemLength = val
                  })
                  setParsedData(newData)
                }}
                onChange={(e) => handleChangeNumberInput(e, true)}
                className={cn.editableCellInput}
              />
            )
          }
          return <span className={cn.propLabel}>{record.itemLength}</span>
        }
      },
    },
    {
      title: 'Ширина, см',
      key: 'itemWidth',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'itemWidth',
            selectedCells,
            setPropertyInOneCellSelected,
            handleClickCell,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            parsedData,
            setParsedData,
            setPropertyInOneCellSelected,
            propertyInOneCellSelected,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'itemWidth'
          ) {
            return (
              <input
                defaultValue={propertyInOneCellSelected as string}
                onBlur={(e) => {
                  const val = e.target.value
                  setPropertyInOneCellSelected(val)
                  const newData = [...parsedData]
                  newData.forEach((n) => {
                    if (n.key == record.key) n.itemWidth = val
                  })
                  setParsedData(newData)
                }}
                onChange={(e) => handleChangeNumberInput(e, true)}
                className={cn.editableCellInput}
              />
            )
          }
          return <span className={cn.propLabel}>{record.itemWidth}</span>
        }
      },
    },
    {
      title: 'Высота, см',
      key: 'itemHeight',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'itemHeight',
            selectedCells,
            setPropertyInOneCellSelected,
            handleClickCell,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            parsedData,
            setParsedData,
            setPropertyInOneCellSelected,
            propertyInOneCellSelected,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'itemHeight'
          ) {
            return (
              <input
                defaultValue={propertyInOneCellSelected as string}
                onBlur={(e) => {
                  const val = e.target.value
                  setPropertyInOneCellSelected(val)
                  const newData = [...parsedData]
                  newData.forEach((n) => {
                    if (n.key == record.key) n.itemHeight = val
                  })
                  setParsedData(newData)
                }}
                onChange={(e) => handleChangeNumberInput(e, true)}
                className={cn.editableCellInput}
              />
            )
          }
          return <span className={cn.propLabel}>{record.itemHeight}</span>
        }
      },
    },
    {
      title: 'Штрихкод',
      key: 'barcode',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'barcode',
            selectedCells,
            setPropertyInOneCellSelected,
            handleClickCell,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            parsedData,
            setParsedData,
            setPropertyInOneCellSelected,
            propertyInOneCellSelected,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'barcode'
          ) {
            return (
              <input
                defaultValue={propertyInOneCellSelected as string}
                onBlur={(e) => {
                  const val = sanitizeBarcode(e.target.value)
                  setPropertyInOneCellSelected(val)
                  const newData = [...parsedData]
                  newData.forEach((n) => {
                    if (n.key == record.key) n.barcode = val
                  })
                  setParsedData(newData)
                }}
                className={cn.editableCellInput}
              />
            )
          }
          const barcodeError = getBarcodeError(String(record.barcode ?? ''))
          return (
            <span
              className={[cn.propLabel, barcodeError ? cn.invalidValue : '']
                .filter(Boolean)
                .join(' ')}
              title={barcodeError ?? undefined}
            >
              {record.barcode}
            </span>
          )
        }
      },
    },
    {
      title: 'Страна происхождения',
      key: 'countryOfOrigin',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'countryOfOrigin',
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            countries,
            parsedData,
            propertyInOneCellSelected,
            setPropertyInOneCellSelected,
            setParsedData,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'countryOfOrigin'
          ) {
            return (
              <HoverElement>
                <AtomSelect
                  options={countries.map((c) => ({
                    value: c.id,
                    label: c.description,
                  }))}
                  openByHover={false}
                  value={
                    propertyInOneCellSelected &&
                    typeof propertyInOneCellSelected !== 'string'
                      ? propertyInOneCellSelected.value
                      : (propertyInOneCellSelected as string)
                  }
                  showSearch
                  handleChange={(_, option: OptionType | OptionType[]) => {
                    if (!Array.isArray(option)) {
                      const newData = [...parsedData]
                      newData.forEach((n) => {
                        if (n.key == record.key)
                          n.countryOfOrigin = {
                            value: option.value,
                            label: option.label,
                          }
                      })
                      setPropertyInOneCellSelected({
                        value: option.value,
                        label: option.label,
                      })
                      setParsedData(newData)
                    }
                  }}
                  handleClear={() => {
                    const newData = [...parsedData]
                    newData.forEach((n) => {
                      if (n.key == record.key)
                        n.countryOfOrigin = { value: '', label: '' }
                    })
                    setPropertyInOneCellSelected(null)
                    setParsedData(newData)
                  }}
                />
              </HoverElement>
            )
          }
          return (
            <span className={cn.propLabel}>
              {renderMatchedValue(record, 'countryOfOrigin')}
            </span>
          )
        }
      },
    },
    {
      title: 'Вес, кг',
      key: 'itemWeight',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'itemWeight',
            selectedCells,
            setPropertyInOneCellSelected,
            handleClickCell,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            parsedData,
            setParsedData,
            setPropertyInOneCellSelected,
            propertyInOneCellSelected,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'itemWeight'
          ) {
            return (
              <input
                defaultValue={propertyInOneCellSelected as string}
                onBlur={(e) => {
                  const val = e.target.value
                  setPropertyInOneCellSelected(val)
                  const newData = [...parsedData]
                  newData.forEach((n) => {
                    if (n.key == record.key) n.itemWeight = val
                  })
                  setParsedData(newData)
                }}
                onChange={(e) => handleChangeNumberInput(e, true)}
                className={cn.editableCellInput}
              />
            )
          }
          return <span className={cn.propLabel}>{record.itemWeight}</span>
        }
      },
    },
    {
      title: 'Гарантия, мес.',
      key: 'warranty',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'warranty',
            selectedCells,
            setPropertyInOneCellSelected,
            handleClickCell,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            parsedData,
            setParsedData,
            setPropertyInOneCellSelected,
            propertyInOneCellSelected,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'warranty'
          ) {
            return (
              <input
                defaultValue={propertyInOneCellSelected as string}
                onBlur={(e) => {
                  const val = e.target.value
                  setPropertyInOneCellSelected(val)
                  const newData = [...parsedData]
                  newData.forEach((n) => {
                    if (n.key == record.key) n.warranty = val
                  })
                  setParsedData(newData)
                }}
                onChange={(e) => handleChangeNumberInput(e, false)}
                className={cn.editableCellInput}
              />
            )
          }
          return <span className={cn.propLabel}>{record.warranty}</span>
        }
      },
    },
    {
      title: 'Единица измерения',
      key: 'unitOfMeasurement',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'unitOfMeasurement',
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            unitsOfMeasurement,
            parsedData,
            propertyInOneCellSelected,
            setPropertyInOneCellSelected,
            setParsedData,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'unitOfMeasurement'
          ) {
            return (
              <HoverElement>
                <AtomSelect
                  options={unitsOfMeasurement.map((u) => ({
                    value: u.id,
                    label: u.description,
                  }))}
                  openByHover={false}
                  value={
                    propertyInOneCellSelected &&
                    typeof propertyInOneCellSelected !== 'string'
                      ? propertyInOneCellSelected.value
                      : (propertyInOneCellSelected as string)
                  }
                  showSearch
                  handleChange={(_, option: OptionType | OptionType[]) => {
                    if (!Array.isArray(option)) {
                      const newData = [...parsedData]
                      newData.forEach((n) => {
                        if (n.key == record.key)
                          n.unitOfMeasurement = {
                            value: option.value,
                            label: option.label,
                          }
                        n.rawUnitOfMeasurement = ''
                      })
                      setPropertyInOneCellSelected({
                        value: option.value,
                        label: option.label,
                      })
                      setParsedData(newData)
                    }
                  }}
                  handleClear={() => {
                    const newData = [...parsedData]
                    newData.forEach((n) => {
                      if (n.key == record.key)
                        n.unitOfMeasurement = { value: '', label: '' }
                    })
                    setPropertyInOneCellSelected(null)
                    setParsedData(newData)
                  }}
                />
              </HoverElement>
            )
          }
          return renderMatchedValue(record, 'unitOfMeasurement')
        }
      },
    },
    {
      title: 'Описание',
      key: 'details',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'details',
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            parsedData,
            propertyInOneCellSelected,
            setPropertyInOneCellSelected,
            setParsedData,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'details'
          ) {
            return (
              <textarea
                defaultValue={propertyInOneCellSelected as string}
                onBlur={(e) => {
                  const val = e.target.value
                  setPropertyInOneCellSelected(val)
                  const newData = [...parsedData]
                  newData.forEach((n) => {
                    if (n.key == record.key) n.details = val
                  })
                  setParsedData(newData)
                }}
                className={cn.editableCellTextarea}
              />
            )
          }
          return (
            <div
              className={
                record.details
                  ? cn.bigTextPropLabel
                  : [cn.bigTextPropLabel, cn.empty].join(' ')
              }
            >
              <span>{record.details || ''}</span>
            </div>
          )
        }
      },
    },
    {
      title: 'Комментарий',
      key: 'comment',
      onCell: (record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          } = getColumnsParams
          return handleOnCell({
            record,
            columnKey: 'comment',
            selectedCells,
            handleClickCell,
            setPropertyInOneCellSelected,
          })
        }
      },
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const {
            oneCellSelected,
            parsedData,
            propertyInOneCellSelected,
            setPropertyInOneCellSelected,
            setParsedData,
          } = getColumnsParams
          if (
            oneCellSelected.rowKey == record.key &&
            oneCellSelected.columnKey == 'comment'
          ) {
            return (
              <textarea
                defaultValue={propertyInOneCellSelected as string}
                onBlur={(e) => {
                  const val = e.target.value
                  setPropertyInOneCellSelected(val)
                  const newData = [...parsedData]
                  newData.forEach((n) => {
                    if (n.key == record.key) n.comment = val
                  })
                  setParsedData(newData)
                }}
                className={cn.editableCellTextarea}
              />
            )
          }
          return (
            <div
              className={
                record.comment
                  ? cn.bigTextPropLabel
                  : [cn.bigTextPropLabel, cn.empty].join(' ')
              }
            >
              <span>{record.comment || ''}</span>
            </div>
          )
        }
      },
    },
    {
      title: '',
      key: 'removalAction',
      width: 50,
      render: (_: undefined, record: NomenclatureType) => {
        if (getColumnsParams) {
          const { removeTableRow } = getColumnsParams
          return (
            <Popconfirm
              title="Удаление ряда таблицы"
              onConfirm={() => removeTableRow(record.key)}
              okText="Да"
              cancelText="Нет"
              overlayClassName={cn.popconfirm}
              align={{ offset: [-24, -16] }}
              icon={null}
            >
              <button className={cn.rowRemovalButton}>
                <DeleteOutlined />
              </button>
            </Popconfirm>
          )
        }
      },
    },
  ] as ColumnType<NomenclatureType>[]

export const getNomenclatureCreatedTableColumns = (params: {
  categoriesTree: CategoryInterface[]
  antdCategoriesTree: AntdTreeType[]
  nomenclatureGroupsTree: NomenclatureGroupInterface[]
  antdNomenclatureGroupsTree: AntdTreeType[]
}) => {
  const {
    categoriesTree,
    antdCategoriesTree,
    nomenclatureGroupsTree,
    antdNomenclatureGroupsTree,
  } = params

  return [
    {
      title: '',
      key: 'nomenclatureEditModal',
      width: 50,
      render: (_: unknown, record: PluralNomenclatureImportResponseType) => {
        if (!record.createdNomenclature) return null

        const modalProduct = {
          nomenclatureDto: record.createdNomenclature,
          metadata: null,
        }

        return (
          <Suspense fallback={null}>
            <NomenclatureEditModal
              nomenclature={modalProduct}
              categoriesTree={categoriesTree}
              antdCategoriesTree={antdCategoriesTree}
              nomenclatureGroupsTree={nomenclatureGroupsTree}
              antdNomenclatureGroupsTree={antdNomenclatureGroupsTree}
            />
          </Suspense>
        )
      },
    },
    { title: 'Код', key: 'code', dataIndex: ['createdNomenclature', 'code'] },
    {
      title: 'Артикул',
      key: 'article',
      dataIndex: ['createdNomenclature', 'article'],
    },
    {
      title: 'Наименование',
      key: 'description',
      dataIndex: ['createdNomenclature', 'description'],
    },
    {
      title: 'Бренд',
      key: 'brand',
      dataIndex: ['createdNomenclature', 'brandDto', 'description'],
    },
    {
      title: 'Категория',
      key: 'category',
      dataIndex: ['createdNomenclature', 'categoryDto', 'description'],
    },
  ]
}

export const getNomenclatureNotCreatedTableColumns = () => [
  { title: 'Артикул', key: 'article', dataIndex: 'article' },
  { title: 'Бренд', key: 'brand', dataIndex: 'brand' },
  {
    title: 'Причина ошибки',
    key: 'errorMessage',
    dataIndex: 'errorMessage',
    render: (text: string) => (
      <span className={cn.errorMessageText}>{text}</span>
    ),
  },
]
